import React, { useEffect, useRef } from 'react';
import { Animated, View, Platform, ViewProps, StyleProp, ViewStyle, StyleSheet } from 'react-native';

/*
    adapted from:
    https://github.com/react-native-elements/react-native-elements/blob/52056ded1e067061c33af6ab328fcf6168652bb1/packages/base/src/Skeleton/Skeleton.tsx
*/

export interface BaseSkeletonProps extends ViewProps {
    /**
     * show circular variant
     */
    circle?: boolean;
    /**
     * Width of Skeleton View
     */
    width?: ViewStyle['width'];
    /**
     * Height of Skeleton View
     * @default 12
     */
    height?: ViewStyle['height'];
    /**
     * Type of animation
     */
    animation?: 'none' | 'pulse' | 'wave';
    /**
     * Custom style for skeleton gradient
     */
    skeletonStyle?: StyleProp<ViewStyle>;
    /**
     * Custom Linear Gradient Component
     * @type React Component
     */
    LinearGradientComponent?: React.ComponentType<any>;
}

export const BaseSkeleton: React.FC<BaseSkeletonProps> = ({
    circle,
    width = '100%',
    height,
    animation = 'pulse',
    style,
    skeletonStyle,
    ...rest
}) => {
    const animationRef = useRef(new Animated.Value(0));
    const animationLoop = useRef<Animated.CompositeAnimation | undefined>(undefined);

    const [layoutWidth, setLayoutWidth] = React.useState<number>(0);

    useEffect(() => {
        animationLoop.current = Animated.timing(animationRef.current, {
            toValue: 2,
            delay: 400,
            duration: 1500,
            useNativeDriver: !!Platform.select({
                web: false,
                native: true,
            }),
        });
        animationRef.current.setValue(0);
        Animated.loop(animationLoop.current).start();
    }, []);

    return (
        <View
            accessibilityRole="none"
            accessibilityLabel="loading..."
            accessible={false}
            onLayout={({ nativeEvent }) => {
                setLayoutWidth(nativeEvent.layout.width);
            }}
            style={[
                styles.container,
                {
                    width: width,
                    height: height || 12,
                    backgroundColor: '#bdc6cf',
                },
                circle && {
                    borderRadius: 50,
                    height: height || width,
                },
                style,
            ]}
            {...rest}
        >
            {animation !== 'none' && (
                <Animated.View
                    style={[
                        styles.skeleton,
                        // Color(theme?.colors?.grey4).darken(0.1).rgb().string(),
                        { backgroundColor: 'rgb(166, 178, 190)' },
                        animation === 'pulse' && {
                            width: '100%',
                            opacity: animationRef.current.interpolate({
                                inputRange: [0, 1, 2],
                                outputRange: [1, 0, 1],
                            }),
                        },
                        animation === 'wave' && {
                            transform: [
                                {
                                    translateX: animationRef.current.interpolate({
                                        inputRange: [0, 2],
                                        outputRange: [-layoutWidth * 2, layoutWidth * 2],
                                    }),
                                },
                            ],
                        },
                        skeletonStyle,
                    ]}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        overflow: 'hidden',
        borderRadius: 2,
    },
    skeleton: {
        height: '100%',
    },
});
